import {
  Field,
  GetStaticComponentProps,
  Text,
  useComponentProps,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { ContentBox, StyledDivider } from './StudyDetail.styles';
import StudyDetailList from './StudyDetailList';
import formatDate from '../../lib/utils/date/formatDate';
import getAgeRange from '../../lib/utils/coveo/getAgeRange';
import UiHeading from '../shared/Heading';
import Head from 'next/head';
import {
  getStudyLocationsByOrgStudyId,
  ProtocolSiteSearchResult,
} from '../../GraphQL/queries/studyLocations.query';

export { getProtocolSitesTemplateId } from '../../GraphQL/queries/studyLocations.query';

export type StudyLocationSchemaItem = {
  '@type': 'AdministrativeArea';
  name: string;
};

type SiteSettings = {
  baseUrl?: { value?: string };
};

const getFieldValue = (field: unknown): string => {
  if (!field || typeof field !== 'object' || !('value' in field)) {
    return '';
  }

  const value = (field as { value?: unknown }).value;
  return typeof value === 'string' ? value : '';
};

const sanitizeForJsonLd = (value?: string): string =>
  (value || '').replace(/</g, '\\u003c').replace(/>/g, '\\u003e');

export const normalizeStudyLocations = (
  siteItems: ProtocolSiteSearchResult[] = []
): StudyLocationSchemaItem[] => {
  return siteItems
    .map((site) => {
      const city = site?.siteCity?.value || '';
      const state =
        site?.siteState?.jsonValue?.name || site?.siteState?.jsonValue?.displayName || '';
      const locationName = [city, state].filter(Boolean).join(', ');

      return {
        '@type': 'AdministrativeArea' as const,
        name: locationName,
      };
    })
    .filter((location) => Boolean(location.name));
};

export const getStaticProps: GetStaticComponentProps = async (_rendering, layoutData) => {
  const orgStudyId = getFieldValue(layoutData?.sitecore?.route?.fields?.orgStudyId);
  const siteItems = orgStudyId ? await getStudyLocationsByOrgStudyId(orgStudyId) : [];
  const studyLocations = normalizeStudyLocations(siteItems);

  return { studyLocations };
};

type StudyDetailProps = ComponentProps & {
  fields: {
    ageLabel: Field<string>;
    completionDateLabel: Field<string>;
    conditionLabel: Field<string>;
    ctGovIdLabel: Field<string>;
    investigationalProductLabel: Field<string>;
    participantLabel: Field<string>;
    phaseLabel: Field<string>;
    sectionTitle: Field<string>;
    sponsorLabel: Field<string>;
    studyNymberLabel: Field<string>;
    treatmentTypeLabel: Field<string>;
  };
};

export type TrialConditionsType = {
  fields: {
    conditionValue: Field<string>;
  };
};

export type FieldValues = {
  minimumAge: Field<string>;
  maximumAge: Field<string>;
  phase: Field<string>;
  enrollment: Field<string>;
  completionDate: Field<string>;
  studyType: Field<string>;
  investigationalProduct: Field<string>;
  source: Field<string>;
  nctId: Field<string>;
  orgStudyId: Field<string>;
  trialConditions: TrialConditionsType[];
  trialTitle: Field<string>;
  trialStatus: Field<string>;
  trialDescription: Field<string>;
};
const StudyDetail = (props: StudyDetailProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { fields, rendering } = props;
  const componentData = useComponentProps<{ studyLocations?: StudyLocationSchemaItem[] }>(
    rendering.uid
  );

  if (!fields) {
    return <></>;
  }
  const fieldValues = sitecoreContext?.route?.fields as unknown as FieldValues;
  const fieldLabels = fields;

  const siteSettings = sitecoreContext?.siteSettings as SiteSettings | undefined;
  const baseUrl = siteSettings?.baseUrl?.value || '';
  const itemPath = sitecoreContext?.itemPath || '';
  const cleanUrl = `${baseUrl}${itemPath}`.replace(/([^:]\/)\/+/g, '$1');
  const sanitizedUrl = sanitizeForJsonLd(cleanUrl);

  const currentUrl = sanitizedUrl || undefined;

  const studyLocation = componentData?.studyLocations?.length ? componentData.studyLocations : [];

  const trials = {
    value: fieldValues?.trialConditions
      ?.map((x: TrialConditionsType) => x.fields?.conditionValue.value)
      .join(','),
  };
  const age = {
    value: getAgeRange(fieldValues?.minimumAge?.value, fieldValues?.maximumAge?.value),
  };
  const formattedDate = {
    value:
      fieldValues?.completionDate?.value &&
      fieldValues?.completionDate?.value !== '0001-01-01T00:00:00Z'
        ? formatDate(fieldValues?.completionDate?.value)
        : '',
  };
  const renderJSONLDSchema = () => {
    // JSON-LD structured data
    const schemaData = {
      '@context': 'https://schema.org/',
      '@type': 'MedicalStudy',
      name: fieldValues?.trialTitle?.value,
      status:
        fieldValues?.trialStatus?.value === 'Enrollment'
          ? 'https://schema.org/Recruiting'
          : `https://schema.org/${fieldValues?.trialStatus?.value}`,
      studySubject: {
        '@type': 'MedicalCondition',
        name: trials?.value,
      },
      sponsor: {
        '@type': 'Organization',
        name: fieldValues?.source?.value,
      },
      identifier: fieldValues?.nctId?.value,
      description: fieldValues?.trialDescription?.value,
      studyLocation: studyLocation.length ? studyLocation : undefined,
      url: currentUrl,
    };

    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>
    );
  };
  return (
    <>
      {renderJSONLDSchema()}
      <ContentBox>
        <UiHeading size="h3" data-testid="title" color="primary" align="center" isChildren>
          <Text field={fieldLabels?.sectionTitle} />
        </UiHeading>
        <StyledDivider />
        <StudyDetailList label={fieldLabels.conditionLabel} value={trials} />
        <StudyDetailList label={fieldLabels.ageLabel} value={age} />
        <StudyDetailList label={fieldLabels.phaseLabel} value={fieldValues?.phase} />
        <StudyDetailList label={fieldLabels.participantLabel} value={fieldValues?.enrollment} />
        <StudyDetailList label={fieldLabels.completionDateLabel} value={formattedDate} />
        <StudyDetailList label={fieldLabels.treatmentTypeLabel} value={fieldValues?.studyType} />
        <StudyDetailList
          label={fieldLabels.investigationalProductLabel}
          value={fieldValues?.investigationalProduct}
        />
        <StyledDivider />
        <StudyDetailList label={fieldLabels.sponsorLabel} value={fieldValues?.source} />
        <StudyDetailList label={fieldLabels.ctGovIdLabel} value={fieldValues?.nctId} />
        <StudyDetailList label={fieldLabels.studyNymberLabel} value={fieldValues?.orgStudyId} />
      </ContentBox>
    </>
  );
};

export default StudyDetail;
